import "@zebra-fed/icons/Enterprise/index.css";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import styled from "styled-components";
import { AuthContext } from "../../authentication/Auth";
import app from "../../authentication/firebaseStart";
import { ContentWrapper, CheckingAccessMessage } from "../../styled-components/shared";
import { CircularProgress } from "@material-ui/core";
import { userJustManager } from "../../data/userUtil";
import ZamsSummaryContainer from "./Components/ZamsSummaryContainer";
import { DashboardProvider, useDashboardContext } from "./context/dashboard.context";
import { DashboardFiltersProvider, useDashboardFiltersContext } from "./context/dashboard-filter.context";
import ProductivityPage from './Productivity/ProductivityPage';
import { useDGAMLicenseCheck } from "../../authentication/licensing";

const firestore = app.firestore();

const ContainerBox = styled.div`
  width: 98%;
  padding: 1%;
  float: left;
  height: auto;
  @media screen and (max-width: 750px) {
    margin-top: 10px;
  }
`;

// Define styled components for the welcome message
const WelcomeMessage = styled.div`
  font-weight: 600;
  font-size: 24px;
  line-height: 28px;
  color: #000000;
`;

// Define styled components for the last updated message
const LastUpdatedMessage = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #1D1E23;
  margin-bottom: 16px;
`;

export const AdminManagerSummaryBoard = ({props}) => {
  const context = useContext(AuthContext);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const { setSitesData, setSelectedSites, setCabinetsData, setSelectedCabinets, setSiteDefaultValue, setCabinetDefaultValue, isResetFilter } = useDashboardFiltersContext();
  const {isLoading, setIsLoading } = useDashboardContext();
  const DEFAULT_ROWS_PER_PAGE = 10;
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const isManager = userJustManager(context.currentUser.claims);
  const [lastUpdated, setLastUpdated] = useState("");
  const { t } = useTranslation();
  const { isDGAMLicensed, loadingLicense } = useDGAMLicenseCheck();

  useEffect(() => {
    const isSSOLoggedIn = localStorage.getItem('ssoLogin') === 'true'
    const emailId = isSSOLoggedIn ? context.currentUser.uid : context.currentUser.email;
    setLoggedInUser(emailId); //For SSO User will get uid and Non SSO user email
    props.setTitle("");
    updateLastUpdated();
    getSitesData(pageSize);
  }, []);

  useEffect(() => {
    getSitesData(pageSize);
  }, [isResetFilter])

  // Function to update the timestamp
  const updateLastUpdated = () => {
    const now = new Date();
    const options = { month: "short", day: "2-digit", year: "numeric" };
    const date = now.toLocaleDateString("en-US", options);
    const time = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    setLastUpdated(`Last Updated: ${date} | ${time}`);
  };

  async function getSitesData(currentPageSize) {
    setIsLoading(true);
    let itemsPerPage =
      currentPageSize; /* Should be fetched dynamically from UI */
    let companyID = context.currentUser.claims.companyID;
    let sites = [];
    let sitesArray = [];

    if (isManager) {
      context.currentUser.claims.siteArr.forEach((el) => {
        sitesArray.push({ label: el, value: el });
      });
    } else {
      sites = (
        await firestore
          .collection("tenants")
          .doc(companyID)
          .collection("SiteLocations")
          // .limit(itemsPerPage)
          .get()
      ).docs.map((doc) => doc.data());
      Object.keys(sites).forEach((site) => {
        sitesArray.push({
          label: sites[site].siteName,
          value: sites[site].siteName
        });
      });
      //sitesArray.unshift({ label: 'Unassigned Devices', value: 'Unassigned Devices' });
      sitesArray.sort(customSortAscending);
      if (sitesArray.length > 0) {
        sitesArray.unshift({ label: "All Sites", value: "select-all" });
      }
    }
    const noDuplicates = removeDuplicatesFromObjectArray(sitesArray, "value");
    setSitesData(noDuplicates);
    setSelectedSites(noDuplicates);
    setSiteDefaultValue(noDuplicates.length > 0 ? noDuplicates[0] : []);
    getCabinetsData(pageSize, sitesArray);
    setIsLoading(false);
  }

  const getCabinetsData = async(currentPageSize, selectedSites = []) => {
    let itemsPerPage = currentPageSize; /* Should be fetched dynamically from UI */
    const companyID = context.currentUser.claims.companyID;
    let cabinetList = [];

    setIsLoading(true);

    try {
      let cabinetsSnapshot;
      if (selectedSites.length > 0) {
        let sitesToQuery = selectedSites.map(site => site.value);
        const isSelectAll = sitesToQuery.includes("select-all");
        if (isSelectAll) {
          cabinetsSnapshot = await firestore
            .collection("tenants")
            .doc(companyID)
            .collection("Kiosk")
            //.limit(itemsPerPage)
            .get();
        } else {
          cabinetsSnapshot = await firestore
            .collection("tenants")
            .doc(companyID)
            .collection("Kiosk")
            .where("siteName", "in", sitesToQuery)
            //.limit(itemsPerPage)
            .get();
        }
        cabinetsSnapshot.forEach((doc) => {
          cabinetList.push({ label: doc.id, value: doc.id });
        });
        cabinetList.sort(customSortAscending);
        if (cabinetList.length > 0) {
          cabinetList.unshift({ label: "All Kiosks", value: "select-all" });
        }
        const noDuplicates = removeDuplicatesFromObjectArray(cabinetList, "value");
        setCabinetsData(noDuplicates);
        setSelectedCabinets(noDuplicates);
        setCabinetDefaultValue(noDuplicates.length > 0? noDuplicates[0] : []);
      } else {
        setCabinetsData([]);
        setSelectedCabinets([]);
        setCabinetDefaultValue([]);
      }
    } catch (error) {
      console.error("Error fetching kiosks:", error);
    }
    setIsLoading(false);
  }

  function customSortAscending(a, b) {
    const aLabel = a?.label || '';
    const bLabel = b?.label || '';

    if (aLabel === '' && bLabel === '') {
        return 0;
    }
    if (aLabel === '') {
        return 1;
    }
    if (bLabel === '') {
        return -1;
    }
    const aIsSpecial = /^[^a-zA-Z]/.test(aLabel);
    const bIsSpecial = /^[^a-zA-Z]/.test(bLabel);

    if (aIsSpecial && !bIsSpecial) return 1;
    if (!aIsSpecial && bIsSpecial) return -1;
    return aLabel.localeCompare(bLabel);
  }

  function removeDuplicatesFromObjectArray(arr, key) {
    return [...new Map(arr.map((item) => [item[key], item])).values()];
  }

  return (
    <>
      <ContentWrapper
        style={{
          cursor: isLoading ? "wait" : "default",
          opacity: isLoading ? "0.4" : "1",
          position: "relative",
          width: "72vw",
          height: "107vh",
        }}
      >
        <ContainerBox>
          {isLoading && <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
            <CircularProgress />
          </div>}
          {isDGAMLicensed && !loadingLicense && (
            <>
              <WelcomeMessage>
                {loggedInUser ? `Welcome ${loggedInUser.split('@')[0]}` : 'Welcome'}
              </WelcomeMessage>
              <LastUpdatedMessage>
                {lastUpdated}
              </LastUpdatedMessage>
              <ZamsSummaryContainer></ZamsSummaryContainer>
            </>
          )}
          <ProductivityPage isDGAMLicensed={isDGAMLicensed} />
        </ContainerBox>
        {/* <BottomNavLinks /> */}
      </ContentWrapper>
    </>
  );
}

export const AdminManagerSummaryBoardContainer = (props) => {
  return (
    <DashboardProvider>
      <DashboardFiltersProvider>
        <AdminManagerSummaryBoard props={props}></AdminManagerSummaryBoard>
      </DashboardFiltersProvider>
    </DashboardProvider>
  )
};
